import { ApplicationController } from "./application_controller";
import flatpickr from "flatpickr";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import { English } from "flatpickr/dist/l10n/default.js";
import Tabulator from "tabulator-tables";
import nerdamer from "nerdamer";

export default class extends ApplicationController {
  static targets = [
    "dailyPriceEditor",
    "seasonInfo", 
    "dailyPriceForm",
    "dateRangeInput",
    "weekdaysSelect",
    "priceInput",
    "vatCheckbox",
    "vatAmount",
    "priceWithVat",
    "touristTaxCheckbox", 
    "touristTaxAmount",
    "extraSumCheckbox",
    "extraSumInput",
    "totalPrice",
    "currencyDisplay",
    "calendarContainer",
    "dailyPricesCalendar",
    "saveButton",
    "roomIdField",
    "seasonIdField",
    "seasonDateIdField",
    "groupTypeField",
    "seasonsTableContainer"
  ]

  connect() {
    this.columnDateMap = {};
    
    this.tooltipTemplate = document.querySelector('#tooltip-price-template');
    
    this.vatPercent = parseFloat(this.dailyPriceEditorTarget.dataset.roomPricesVatPercent) || 0;
    this.touristTaxPercent = parseFloat(this.dailyPriceEditorTarget.dataset.roomPricesTouristTaxPercent) || 0;
  }

  openDailyEditor(event) {
    const button = event.currentTarget;
    const data = button.dataset;


    // Set form data from button attributes
    this.roomIdFieldTarget.value = data.roomId || '';
    this.seasonIdFieldTarget.value = data.seasonId || '';
    this.seasonDateIdFieldTarget.value = data.seasonDateId || '';
    this.groupTypeFieldTarget.value = data.groupType || '';

    // Store tourist tax value for calculations
    this.touristTaxFixedValue = parseFloat(data.touristTaxValue) || 0;

    // Update season info display
    this.seasonInfoTarget.textContent = `${data.seasonName} - ${data.groupName}`;

    // Set date range
    const startDate = data.startDate ? new Date(data.startDate) : null;
    const endDate = data.endDate ? new Date(data.endDate) : null;
    
    this.dateRangeInputTarget.value = `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;

    // Store season dates for calendar
    this.seasonStartDate = startDate;
    this.seasonEndDate = endDate;

    // Reset weekdays selection to all days
    this.resetWeekdaysSelection();

    // reset price and options
    this.resetPriceAndOptions();

    // Initialize flatpickr
    this.initializeDatePicker(startDate, endDate);

    // Load existing prices for calendar
    this.loadCalendarPrices();
    
    // Calculate initial values
    this.calculatePrice();

    // Show the editor
    this.dailyPriceEditorTarget.classList.remove('d-none');
  }

  resetWeekdaysSelection() {
    const weekdaysSelect = this.weekdaysSelectTarget;
    
    weekdaysSelect.choices.removeActiveItems();
  }

  initializeDatePicker(defaultStart, defaultEnd) {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }

    // Set locale
    let app_locale = this.getAppLocale();
    let locales = {
      "ru": {
        locales: Russian,
        date_format: "d.m.Y"
      },
      "en": {
        locales: English,
        date_format: "Y-m-d"
      }
    };

    // Locale Options
    const locale_options = locales[app_locale];

    this.flatpickrInstance = flatpickr(this.dateRangeInputTarget, {
      mode: "range",
      dateFormat: locale_options["date_format"],
      defaultDate: [defaultStart, defaultEnd],
      minDate: defaultStart,
      maxDate: defaultEnd,
      locale: locale_options["locales"]
    });
  }

  resetPriceAndOptions() {
    // Use AutoNumeric API to properly set the value to 0
    // This prevents the old value from being restored on hover/focus
    if (this.priceInputTarget.numeric && typeof this.priceInputTarget.numeric.set === 'function') {
      this.priceInputTarget.numeric.set(0);
    } else {
      this.priceInputTarget.value = '0';
    }
    
    this.vatCheckboxTarget.checked = false;
    this.touristTaxCheckboxTarget.checked = true;
    this.extraSumCheckboxTarget.checked = false;
    this.extraSumInputTarget.value = '';
    this.extraSumInputTarget.disabled = true;
  }

  calculatePrice() {
    // Calculating all values locally for the daily price editor
    const price = this.parsePrice(this.priceInputTarget.value);
    const isVat = this.vatCheckboxTarget.checked;
    const isTouristTax = this.touristTaxCheckboxTarget.checked;
    const isExtraSum = this.extraSumCheckboxTarget.checked;
    
    // Calculating VAT
    const vatPercent = this.getVatPercentFromHeader();
    const vatValue = isVat ? (price * vatPercent / 100) : 0;
    const priceWithVat = price + vatValue;
    
    const touristTaxValue = isTouristTax ? this.getTouristTaxFixedValue() : 0;
    
    const extraSumFormula = this.extraSumInputTarget.value;
    const extraSum = isExtraSum ? this.calculateExtraSum(extraSumFormula, priceWithVat) : 0;
    
    const totalPrice = priceWithVat + touristTaxValue + extraSum;
    
    this.updatePriceDisplay(vatValue, priceWithVat, touristTaxValue, extraSum, totalPrice);
  }

  priceChange(event) {
    this.calculatePrice();
  }

  isVatChange(event) {
    this.calculatePrice();
  }

  isTouristTaxChange(event) {
    this.calculatePrice();
  }

  isExtraSumChange(event) {
    const isChecked = event.target.checked;
    this.extraSumInputTarget.disabled = !isChecked;
    if (!isChecked) {
      this.extraSumInputTarget.value = "";
    }
    this.calculatePrice();
  }

  extraSumFormulaChange(event) {
    this.calculatePrice();
  }

  priceWithVatChange(event) {
    const priceWithVat = this.parsePrice(event.target.value);
    const isVat = this.vatCheckboxTarget.checked;
    const vatPercent = this.getVatPercentFromHeader();
    
    let price;
    if (isVat && vatPercent > 0) {
      price = priceWithVat / (1 + vatPercent / 100);
    } else {
      price = priceWithVat;
    }
    
    // Use AutoNumeric API to set value properly
    if (this.priceInputTarget.numeric && typeof this.priceInputTarget.numeric.set === 'function') {
      this.priceInputTarget.numeric.set(price);
    } else {
      this.priceInputTarget.value = this.formatCurrency(price);
    }
    
    this.calculatePrice();
  }

  async loadCalendarPrices() {
    const roomId = this.roomIdFieldTarget.value;
    const seasonId = this.seasonIdFieldTarget.value;
    const groupType = this.groupTypeFieldTarget.value;

    // For new rooms without ID, just initialize empty calendar
    if (!roomId) {
      this.initializeCalendarGrid([]);
      return;
    }

    if (!seasonId || !groupType) {
      this.initializeCalendarGrid([]);
      return;
    }

    try {
      const response = await fetch(`/suppliers/${this.getServiceType()}/${this.getSupplierId()}/rooms/${roomId}/room_prices?season_id=${seasonId}&group_type=${groupType}`);
      
      if (!response.ok) {
        this.initializeCalendarGrid([]);
        return;
      }
      
      const data = await response.json();
      const tourist_tax_value = data['room_tourist_tax_value'] || 0;

      this.updatePriceDisplay(0, 0, tourist_tax_value, 0, tourist_tax_value);
      this.touristTaxFixedValue = tourist_tax_value;
      this.initializeCalendarGrid(data.prices || []);
    } catch (error) {
      console.error('Error loading prices:', error);
      this.initializeCalendarGrid([]);
    }
  }

  initializeCalendarGrid(prices) {
    if (this.tabulatorInstance) {
      this.tabulatorInstance.destroy();
    }

    // Always use season dates for calendar display
    if (!this.seasonStartDate || !this.seasonEndDate) {
      return;
    }

    const startDate = new Date(this.seasonStartDate);
    const endDate = new Date(this.seasonEndDate);
    
    // Create price lookup map
    const priceMap = {};
    prices.forEach(price => {
      priceMap[price.date] = price;
    });

    // Generate calendar columns and data
    const { columns, data } = this.createMonthCalendarStructure(startDate, endDate, priceMap);

    this.tabulatorInstance = new Tabulator(this.dailyPricesCalendarTarget, {
      data: data,
      layout: "fitColumns",
      columns: columns,
      height: "200px",
      headerSort: false,
      resizableRows: false,
      resizableColumns: false
    });
  }

  createMonthCalendarStructure(startDate, endDate, priceMap) {
    const { months, dayColumns } = this.generateMonthsAndDays(startDate, endDate, priceMap);
    const columns = this.generateCalendarColumns(months);
    const data = this.generateCalendarData(dayColumns);
    
    return { columns, data };
  }

  generateMonthsAndDays(startDate, endDate, priceMap) {
    const months = {};
    const dayColumns = [];
    
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
      const monthName = currentDate.toLocaleDateString(this.getAppLocale(), { month: 'long', year: 'numeric' });
      
      if (!months[monthKey]) {
        months[monthKey] = {
          name: monthName,
          days: []
        };
      }
      
      const dayData = this.createDayData(currentDate, priceMap, dayColumns.length);
      months[monthKey].days.push(dayData);
      dayColumns.push(dayData);
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return { months, dayColumns };
  }

  createDayData(currentDate, priceMap, index) {
    const dayKey = currentDate.toISOString().split('T')[0];
    const dayName = currentDate.toLocaleDateString(this.getAppLocale(), { weekday: 'short' });
    const dayNum = currentDate.getDate();
    const price = priceMap[dayKey];
    const columnId = `day_${index}`;
    
    // Keeping the column and date matching
    this.columnDateMap[columnId] = dayKey;
    
    // Saving price data for tooltips
    if (!this.priceDataMap) {
      this.priceDataMap = {};
    }
    this.priceDataMap[columnId] = price;
    
    return {
      date: new Date(currentDate),
      dayName: dayName,
      dayNum: dayNum,
      price: price ? (price.total_price_formatted || '0') : '0',
      priceData: price,
      columnId: columnId
    };
  }

  generateCalendarColumns(months) {
    // Create Tabulator column groups
    const columns = [
      {
        title: this.dailyPriceEditorTarget.dataset.monthHeader,
        columns: [{
          title: this.dailyPriceEditorTarget.dataset.weekdaysHeader,
          field: "rowType",
          width: 150,
          formatter: (cell) => {
            const rowData = cell.getRow().getData();
            return rowData.label || '';
          }
        }]
      }
    ];
    
    // Add month groups
    Object.keys(months).forEach(monthKey => {
      const month = months[monthKey];
      const monthColumns = month.days.map(day => this.createDayColumn(day));
      
      columns.push({
        title: month.name,
        columns: monthColumns
      });
    });
    
    return columns;
  }

  createDayColumn(day) {
    const dayOfWeek = day.date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    return {
      title: `${day.dayName} (${day.dayNum.toString().padStart(2, '0')})`,
      field: day.columnId,
      width: 140,
      headerSort: false,
      cssClass: isWeekend ? 'weekend-column' : '',
      formatter: this.createCellFormatter(day)
    };
  }

  createCellFormatter(dayColumn) {
    return (cell) => {
      const rowData = cell.getRow().getData();
      const value = rowData[dayColumn.columnId];
      const numericValue = parseFloat(value) || 0;
      
      const cellElement = cell.getElement();
      cellElement.classList.remove('price-zero', 'price-set');
      if (numericValue === 0) {
        cellElement.classList.add('price-zero');
      } else {
        cellElement.classList.add('price-set');
      }
      
      const priceData = this.priceDataMap[dayColumn.columnId];
      const priceValue = value || '0';
      
      if (priceData) {
        setTimeout(() => {
          this.addTooltipToCell(cellElement, priceData, dayColumn.columnId);
        }, 0);
        
        return `
          <span class="price-with-info">
            ${priceValue}
            <span class="font-info ml-1" data-tooltip-target="${dayColumn.columnId}">
              <i class="fas fa-info-circle"></i>
            </span>
          </span>
        `;
      }
      
      return priceValue;
    };
  }

  generateCalendarData(dayColumns) {
    return [
      {
        id: 1,
        rowType: "prices", 
        label: this.dailyPriceEditorTarget.dataset.pricesLabel,
        ...dayColumns.reduce((acc, day) => {
          acc[day.columnId] = day.price;
          return acc;
        }, {})
      }
    ];
  }


  async updateLocalPrices() {
    const roomId = await this.ensureRoomExists();
    if (!roomId) return;

    await this.savePricesForRoom(roomId);
  }

  async ensureRoomExists() {
    let roomId = this.roomIdFieldTarget.value;
    
    if (!roomId) {
      roomId = await this.createRoomIfNeeded();
    }
    
    return roomId;
  }

  async createRoomIfNeeded() {
    const confirmMessage = this.dailyPriceEditorTarget.dataset.confirmMessage;
    const confirmed = confirm(confirmMessage);
    if (!confirmed) return null;
    
    this.setButtonState('creating');
    
    const roomForm = document.querySelector('#supplier_room_form');
    if (!roomForm) {
      this.resetButtonState();
      return null;
    }
    
    const existingRoomId = this.findExistingRoomId(roomForm);
    if (existingRoomId) {
      this.handleRoomIdUpdate(existingRoomId);
      return existingRoomId;
    }
    
    return await this.createNewRoom(roomForm);
  }

  findExistingRoomId(form) {
    const roomIdInput = form.querySelector('input[name="id"]') || 
                       form.querySelector('input[name="room[id]"]');
    return roomIdInput?.value || null;
  }

  async createNewRoom(roomForm) {
    try {
      const formData = this.prepareRoomFormData(roomForm);
      const result = await this.submitRoomCreation(roomForm, formData);
      
      if (!result.success) {
        this.showError(result.errors.join(', '));
        this.resetButtonState();
        return null;
      }
      
      const roomId = result.room_id;
      this.handleRoomIdUpdate(roomId);
      this.convertFormToEdit(roomForm, roomId);
      this.updateRoomsTable(roomId);
      this.setButtonState('updating');
      
      return roomId;
    } catch (error) {
      console.error(error);
      this.resetButtonState();
      return null;
    }
  }

  prepareRoomFormData(roomForm) {
    const formData = new FormData(roomForm);
    
    // Remove room_prices_attributes - base prices should be created via regular form saving
    const keysToDelete = [];
    for (let [key] of formData.entries()) {
      if (key.includes('room_prices_attributes')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => formData.delete(key));
    
    return formData;
  }

  async submitRoomCreation(roomForm, formData) {
    const response = await fetch(roomForm.action, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: formData
    });
    
    return await response.json();
  }
  async savePricesForRoom(roomId) {
    const formData = this.collectFormData();
    this.setButtonState('updating');

    try {
      const response = await fetch(`/suppliers/${this.getServiceType()}/${this.getSupplierId()}/rooms/${roomId}/room_prices/bulk_update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ room_price: formData })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        this.showSuccess(result.message || this.dailyPriceEditorTarget.dataset.pricesUpdated);
        
        await this.loadCalendarPrices();
        
        await this.refreshSeasonsTable();

        await this.resetWeekdaysSelection();
        await this.resetPriceAndOptions();
        
        this.reinitializeSeasonsFilter();
      } else {
        this.showError(result.message || result.errors?.join(', '));
      }
    } catch (error) {
      this.showError(this.dailyPriceEditorTarget.dataset.errorSave);
    } finally {
      this.resetButtonState();
    }
  }

  collectFormData() {
    if (!this.hasDateRangeInputTarget || !this.hasWeekdaysSelectTarget) {
      return null;
    }
    
    const dateRange = this.dateRangeInputTarget.value.split(/\s*[-—]\s*/);
    let weekDays = Array.from(this.weekdaysSelectTarget.selectedOptions)
      .map(option => option.value);
    
    const allWeekDays = ['0', '1', '2', '3', '4', '5', '6'];
    if (weekDays.length === 0 || weekDays.length === 7) {
      weekDays = allWeekDays;
    }

    const startDate = dateRange[0] ? this.parseDate(dateRange[0]) : null;
    const endDate = dateRange[1] ? this.parseDate(dateRange[1]) : startDate;

    const price = this.parsePrice(this.hasPriceInputTarget ? this.priceInputTarget.value : '0');
    const isVat = this.hasVatCheckboxTarget ? this.vatCheckboxTarget.checked : false;
    const isExtraSum = this.hasExtraSumCheckboxTarget ? this.extraSumCheckboxTarget.checked : false;
    const extraSumFormula = this.hasExtraSumInputTarget ? this.extraSumInputTarget.value : '';
    
    let extraSum = 0;
    if (isExtraSum && extraSumFormula) {
      const priceWithVat = this.calculatePriceWithVat(price, isVat);
      extraSum = this.calculateExtraSum(extraSumFormula, priceWithVat);
    }

    return {
      room_id: this.roomIdFieldTarget.value,
      season_id: this.seasonIdFieldTarget.value,
      group_type: this.groupTypeFieldTarget.value,
      start_date: startDate,
      end_date: endDate,
      week_days: weekDays,
      price: price.toString(),
      is_vat: isVat,
      is_tourist_tax: this.hasTouristTaxCheckboxTarget ? this.touristTaxCheckboxTarget.checked : false,
      is_extra_sum: isExtraSum,
      extra_sum: extraSum.toString(),
      extra_sum_formula: extraSumFormula,
      is_published: true,
      room_attributes: this.getRoomAttributes()
    };
  }


  formatDate(date) {
    return date.toLocaleDateString(this.getAppLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  calculatePriceWithVat(price, isVat) {
    if (!isVat) return price;
    
    const vatPercent = this.getVatPercentFromHeader();
    return price + (price * vatPercent / 100);
  }

  calculateExtraSum(formula, basePrice) {
    if (!formula) return 0;
    
    try {
      // Use nerdamer library for mathematical expression evaluation
      let e = nerdamer(formula);
      let formula_value = e.evaluate();
      return parseFloat(formula_value) || 0;
    } catch (e) {
      // Fallback for simple cases
      try {
        if (formula.includes('%')) {
          const percent = parseFloat(formula.replace('%', '')) || 0;
          return basePrice * percent / 100;
        } else {
          return parseFloat(formula) || 0;
        }
      } catch (fallbackError) {
        return 0;
      }
    }
  }

  parseDate(dateStr) {
    const parts = dateStr.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  getRoomId() {
    return this.dailyPriceEditorTarget.dataset.roomPricesRoomId;
  }

  getSupplierId() {
    return this.dailyPriceEditorTarget.dataset.roomPricesSupplierId;
  }
  
  getServiceType() {
    return this.dailyPriceEditorTarget.dataset.roomPricesServiceType || 'hotels';
  }

  showError(message) {
    window.vNotify.error({
      text: message
    });
  }

  showSuccess(message) {
    window.vNotify.success({
      text: message
    });
  }

  addTooltipToCell(cellElement, priceData, columnId) {
    const iconElement = cellElement.querySelector(`[data-tooltip-target="${columnId}"]`);
    if (!iconElement) return;
    
    const tooltipContent = this.createTooltipElement(priceData);
    
    iconElement.setAttribute('data-controller', 'layout--tooltip');
    iconElement.setAttribute('data-target', 'layout--tooltip.tooltip');
    iconElement.setAttribute('data-bs-toggle', 'tooltip');
    iconElement.setAttribute('data-bs-placement', 'top');
    iconElement.setAttribute('data-bs-html', 'true');
    
    iconElement.setAttribute('title', tooltipContent.outerHTML);
  }
  
  updatePriceDisplay(vatValue, priceWithVat, touristTaxValue, extraSum, totalPrice) {
    if (this.hasVatAmountTarget) {
      this.vatAmountTarget.textContent = this.formatCurrency(vatValue);
    }
    
    if (this.hasPriceWithVatTarget) {
      this.priceWithVatTarget.numeric.set(priceWithVat);
    }
    
    if (this.hasTouristTaxAmountTarget) {
      this.touristTaxAmountTarget.textContent = this.formatCurrency(touristTaxValue);
    }
    
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = this.formatCurrency(totalPrice);
    }
  }
  
  getVatPercentFromHeader() {
    return this.vatPercent;
  }
  
  getTouristTaxPercentFromHeader() {
    return this.touristTaxPercent;
  }
  
  getTouristTaxFixedValue() {
    return this.touristTaxFixedValue !== undefined 
      ? this.touristTaxFixedValue 
      : parseFloat(this.dailyPriceEditorTarget.dataset.roomPricesTouristTaxValue) || 0;
  }

  getRoomAttributes() {
    const roomForm = document.querySelector('#supplier_room_form');
    if (!roomForm) return {};

    const formData = new FormData(roomForm);
    const roomAttributes = {};

    // Get all room[*] fields
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('room[') && !key.includes('room_prices_attributes')) {
        const fieldName = key.replace('room[', '').replace(']', '');
        roomAttributes[fieldName] = value;
      }
    }

    return roomAttributes;
  }

  createTooltipElement(priceData) { 
    if (!priceData || !this.tooltipTemplate) return null;
    
    const tooltipElement = this.tooltipTemplate.cloneNode(true);
    
    const updates = {
      '.season-name': priceData.season?.name || this.dailyPriceEditorTarget.dataset.seasonDefault,
      '.base-price': priceData.price_formatted || '0',
      '.price-with-vat': priceData.price_with_vat_formatted || '0',
      '.total-price': priceData.total_price_formatted || '0',
      '.vat-percent': `${priceData.vat_percent}%`,
      '.tourist-tax-percent': `${priceData.tourist_tax_percent}%`
    };
    
    const conditionalUpdates = [
      {
        selector: '.vat-value',
        condition: priceData.is_vat,
        activeValue: priceData.vat_value_formatted || '0',
        disabledSelector: '.vat-disabled'
      },
      {
        selector: '.tourist-tax-value',
        condition: priceData.is_tourist_tax,
        activeValue: priceData.tourist_tax_value_formatted || '0',
        disabledSelector: '.tourist-tax-disabled'
      },
      {
        selector: '.extra-sum-value',
        condition: priceData.is_extra_sum,
        activeValue: priceData.extra_sum_formatted || '0',
        disabledSelector: '.extra-sum-disabled'
      }
    ];

    Object.entries(updates).forEach(([selector, value]) => {
      const element = tooltipElement.querySelector(selector);
      if (element) element.textContent = value;
    });
    
    conditionalUpdates.forEach(({ selector, condition, activeValue, disabledSelector }) => {
      const element = tooltipElement.querySelector(selector);
      if (element) {
        element.textContent = condition ? 
          activeValue : 
          (tooltipElement.querySelector(disabledSelector)?.textContent || '0');
      }
    });
    
    tooltipElement.style.display = '';
    return tooltipElement;
  }
  

  async refreshSeasonsTable() {
    try {
      this.setLoadingState(true);

      const roomId = this.roomIdFieldTarget.value || this.getRoomId();
      const serviceType = this.getServiceType();
      const supplierId = this.getSupplierId();

      if (!roomId || !serviceType || !supplierId) {
        return;
      }

      const response = await fetch(`/suppliers/${serviceType}/${supplierId}/rooms/${roomId}/room_prices/refresh_seasons_table?service_type=${serviceType}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      
      if (this.hasSeasonsTableContainerTarget) {
        this.seasonsTableContainerTarget.outerHTML = html;
      }
    } catch (error) {
      console.error('Error refreshing seasons table:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    if (this.hasSeasonsTableContainerTarget) {
      if (isLoading) {
        this.seasonsTableContainerTarget.style.opacity = '0.5';
        this.seasonsTableContainerTarget.style.pointerEvents = 'none';
      } else {
        this.seasonsTableContainerTarget.style.opacity = '1';
        this.seasonsTableContainerTarget.style.pointerEvents = 'auto';
      }
    }
  }

  formatCurrency(amount) {
    const localeCode = this.getAppLocale() === 'ru' ? 'ru-RU' : 'en-US';
    return new Intl.NumberFormat(localeCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(amount).replace(/\u00A0/g, ' ');
  }

  parsePrice(value) {
    if (!value) return 0;
    return parseFloat(String(value).replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '')) || 0;
  }

  getAppLocale() {
    return document.querySelector('body').dataset.lang || 'ru';
  }

  setButtonState(state) {
    this.saveButtonTarget.disabled = true;
    
    switch(state) {
      case 'creating':
        this.saveButtonTarget.textContent = this.getCreatingText();
        break;
      case 'updating':
        this.saveButtonTarget.textContent = this.getUpdatingText();
        break;
    }
  }

  resetButtonState() {
    this.saveButtonTarget.disabled = false;
    this.saveButtonTarget.textContent = this.getOriginalButtonText();
  }

  handleRoomIdUpdate(roomId) {
    this.roomIdFieldTarget.value = roomId;
    
    if (this.hasDailyPriceEditorTarget) {
      this.dailyPriceEditorTarget.dataset.roomPricesRoomId = roomId;
    }
    
    if (!this.seasonIdFieldTarget.value) {
      // Initialize empty calendar grid with current month
      this.initializeCalendarGrid([]);
    } else {
      // Refresh calendar if season is already selected
      this.loadCalendarPrices();
    }
    
    const successMessage = this.dailyPriceEditorTarget.dataset.successMessage;
    this.showSuccess(successMessage);
  }

  getOriginalButtonText() {
    return this.saveButtonTarget.dataset.originalText;
  }

  getCreatingText() {
    return this.saveButtonTarget.dataset.creatingText;
  }

  getUpdatingText() {
    return this.saveButtonTarget.dataset.updatingText;
  }

  convertFormToEdit(form, roomId) {
    const currentAction = form.action;
    
    // Changing the action to update URL
    const newAction = currentAction.replace(/\/rooms$/, `/rooms/${roomId}`);
    form.action = newAction;
    
    let methodField = form.querySelector('input[name="_method"]');
    if (!methodField) {
      methodField = document.createElement('input');
      methodField.type = 'hidden';
      methodField.name = '_method';
      form.appendChild(methodField);
    }
    methodField.value = 'patch';
  }

  updateRoomsTable(roomId) {
    if (document.getElementById("rooms_table")) {
      const table = document.getElementById("rooms_table");
      const serviceType = this.getServiceType();
      const supplierId = this.getSupplierId();
      const tableUrl = `/ru/suppliers/${serviceType}/${supplierId}/rooms.json`;
      
      table.tabulator.setData(tableUrl).then(function(){
        table.tabulator.scrollToRow(roomId, "top", true);
      });
    }
  }

  reinitializeSeasonsFilter() {
    const showAllSeasonsCheckbox = document.querySelector('#room_show_all_seasons');
    const hiddenSeasonElements = document.querySelectorAll('[data-target="suppliers--rooms.season_hidden_row"]');
    
    if (showAllSeasonsCheckbox && hiddenSeasonElements.length > 0) {
      if (showAllSeasonsCheckbox.checked) {
        hiddenSeasonElements.forEach(el => {
          el.classList.remove("season-hidden-column");
          el.classList.add("show-all-seasons");
        });
      } else {
        hiddenSeasonElements.forEach(el => {
          el.classList.add("season-hidden-column");
          el.classList.remove("show-all-seasons");
        });
      }
    }
  }

  disconnect() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
    if (this.tabulatorInstance) {
      this.tabulatorInstance.destroy();
    }
  }
}